import React, { useEffect, useContext, useState } from 'react';
import { Form, Col, Row } from 'react-bootstrap';
import { useParams } from 'react-router';
import AppContext from 'Utils/AppContext';
import { pickerCloudAccounts } from 'CloudAccounts/api';
import ProdToggle from './ProdToggle';

// Compute which authMode options to offer given deployment cloud + available auth clouds.
// PASSWORD is always available; IAM/Entra are gated by deployment + registration state.
const buildAuthModeOptions = (availableAuthClouds) => {
  const opts = [
    { value: 'PASSWORD', label: 'Password', helper: 'Use the username + password supplied below.' }
  ];
  if (availableAuthClouds && availableAuthClouds.includes('aws')) {
    opts.push({
      value: 'AWS_IAM',
      label: 'AWS IAM (RDS)',
      helper: 'Container\'s IAM role mints a short-lived RDS auth token at connect time. The DB user must be mapped to the IAM identity (e.g. `GRANT rds_iam TO <user>` on Postgres).'
    });
  }
  if (availableAuthClouds && availableAuthClouds.includes('azure')) {
    opts.push({
      value: 'AZURE_ENTRA',
      label: 'Azure Entra (Managed Identity)',
      helper: 'Container\'s Managed Identity obtains an Entra access token at connect time. The DB must have the MI added as a user (AAD user on Azure SQL, aad_user on Postgres/MySQL flexible server).'
    });
  }
  return opts;
};

const DbSourceEditor = ({ type, connection, setConnection }) => {
  const { rulesList, availableAuthClouds } = useContext(AppContext);
  const { id } = useParams();

  const defaultRules = rulesList.find(rules => rules.default === true);

  // FIELDS = host/port/database/schema; JDBC_URL = single URL string. Username/password show in
  // both. Initial mode is inferred by the API mapper (toUiDataSource) so opening an existing
  // URL-driven row starts in URL mode without an extra click.
  const connectionMode = connection._connectionMode || 'FIELDS';
  const isJdbcUrlMode = connectionMode === 'JDBC_URL';
  const setConnectionMode = (mode) => setConnection({ ...connection, _connectionMode: mode });

  const authModeOptions = buildAuthModeOptions(availableAuthClouds);
  // Only render the dropdown when there's a real choice. Single option (Password only)
  // collapses to the standard password field with no extra UI surface.
  const showAuthModeDropdown = authModeOptions.length > 1;
  const authMode = connection.authMode || 'PASSWORD';
  const selectedOption = authModeOptions.find(o => o.value === authMode) || authModeOptions[0];
  const passwordRequired = authMode === 'PASSWORD';
  const showRegionField = authMode === 'AWS_IAM';
  const showCredentialSource = authMode === 'AWS_IAM' || authMode === 'AZURE_ENTRA';
  const isSqlServer = type === 'mssql';
  // Username is only structurally required for PASSWORD auth. In IAM/Entra modes the
  // token carries identity and whether a username is also needed depends on the engine
  // (Azure SQL ignores it; Postgres/MySQL use it as the AAD UPN). We don't pre-judge that
  // here — the worker is the authority and returns a clear error if it's actually missing.
  const usernameRequired = passwordRequired;
  // MS JDBC v10+ defaults: encrypt=true, trustServerCertificate=false. Persist explicit booleans
  // so the API/worker URL builders can read them without conflating "not set" with "false".
  const encrypt = connection.encrypt !== undefined ? connection.encrypt : true;
  const trustServerCertificate = connection.trustServerCertificate === true;

  // Alternate credentials offered alongside "Default (platform identity)" when the selected
  // authMode is IAM/Entra. Filtered to the cloud that matches authMode — AWS_IAM shows AWS
  // entries only, AZURE_ENTRA shows Azure only.
  const [alternateCredentials, setAlternateCredentials] = useState([]);
  useEffect(() => {
    if (!showCredentialSource) return;
    let cancelled = false;
    pickerCloudAccounts()
      .then((rows) => { if (!cancelled) setAlternateCredentials(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (!cancelled) setAlternateCredentials([]); });
    return () => { cancelled = true; };
  }, [showCredentialSource]);
  const credentialSourceOptions = alternateCredentials.filter((a) =>
    (authMode === 'AWS_IAM' && a.cloud === 'AWS')
    || (authMode === 'AZURE_ENTRA' && a.cloud === 'AZURE')
  );

  const defaults = {
    database: '',
    host: '',
    name: '',
    password: '',
    port: '3306',
    user: '',
    type: 'mysql',
    authMode: 'PASSWORD',
    ...!id && ({
      scheduleEnabled: true,
      scheduleDayOfWeek: 'SAT',
      scheduleWeekOccurrence: 1,
      scheduleHour: 0,
      scheduleMinute: 0,
      scheduleRules: defaultRules
    })
  };

  // Merge defaults UNDER any fields the parent SourceForm dropdown already populated
  // (type / customDatabaseType / port). A plain `setConnection(defaults)` here would
  // wipe those out, fall back to the defaults' `type: 'mysql'`, and — if no MySQL
  // driver is installed — leave SourceForm with selectedOption=null and no editor.
  useEffect(() => { if (!id) setConnection((prev) => ({ ...defaults, ...(prev || {}) })); }, []);
  
  return (
    <div className='text-left'>
      <Row className='mb-3'>
        <Col>
          <Form.Label>Data source name *</Form.Label>
          <Form.Control
            size='sm'
            placeholder='Sample data source'
            value={connection.name}
            onChange={(n) => setConnection({ ...connection, name: n.target.value })}
            type='text'
            required
            isInvalid={connection && connection.name && connection.name.includes(' ')}
          />
          <Form.Control.Feedback type="invalid">
            Data source names must not include spaces.
          </Form.Control.Feedback>
        </Col>
      </Row>
      <ProdToggle />

      <Row className='mb-3'>
        <Col lg='12'>
          <Form.Label>Connection details</Form.Label>
          <div>
            <Form.Check
              inline
              type='radio'
              id='conn-mode-fields'
              name='conn-mode'
              label='Host, port, database'
              checked={!isJdbcUrlMode}
              onChange={() => setConnectionMode('FIELDS')}
            />
            <Form.Check
              inline
              type='radio'
              id='conn-mode-jdbc'
              name='conn-mode'
              label='JDBC URL'
              checked={isJdbcUrlMode}
              onChange={() => setConnectionMode('JDBC_URL')}
            />
          </div>
          <Form.Text className='text-muted'>
            {isJdbcUrlMode
              ? 'Provide a complete JDBC URL. Username and password below are still applied at connect time.'
              : 'Pick this if you have a host, port, and database name. Switch to JDBC URL for cases the form fields don\u2019t cover (clusters, custom params, non-standard schemes).'}
          </Form.Text>
        </Col>
      </Row>

      {isJdbcUrlMode ? (
        <Row className='mb-3'>
          <Col lg='12'>
            <Form.Label>JDBC URL *</Form.Label>
            <Form.Control
              size='sm'
              type='text'
              value={connection.jdbcUrl ?? ''}
              onChange={(n) => setConnection({ ...connection, jdbcUrl: n.target.value })}
              placeholder='jdbc:postgresql://host:5432/db?ssl=true'
              required
            />
            <Form.Text className='text-muted'>
              Used verbatim. The driver is still chosen by the data source type selector above.
            </Form.Text>
          </Col>
        </Row>
      ) : (
        <>
          <Row className='mb-3'>
            <Col lg='8' className='pr-1'>
              <Form.Label>Host *</Form.Label>
              <Form.Control
                size='sm'
                placeholder='my-db-instance.us-east-2.rds.amazonaws.com'
                value={connection.host || ''}
                onChange={(n) => setConnection({ ...connection, host: n.target.value })}
                type='text'
                required
              />
              {/^\s*https?:\/\//i.test(connection.host || '') || /\/\s*$/.test(connection.host || '') ? (
                <Form.Text className='text-warning'>
                  Host should be a bare hostname &mdash; a leading{' '}
                  <code>http://</code> or <code>https://</code> or a trailing{' '}
                  <code>/</code> probably shouldn&rsquo;t be here and can break the connection.
                </Form.Text>
              ) : null}
            </Col>
            <Col lg='4' className='pl-1'>
              <Form.Label>Port *</Form.Label>
              <Form.Control
                size='sm'
                value={connection.port || ''}
                onChange={(n) => setConnection({ ...connection, port: n.target.value })}
                type='number'
                placeholder='Port'
                required
              />
            </Col>
          </Row>
          <Row className='mb-3'>
            <Col lg='12'>
              <Form.Label>{type === 'oracledb' ? 'SERVICE NAME' : 'DATABASE'} *</Form.Label>
              <Form.Control
                size='sm'
                value={connection.database || ''}
                onChange={(n) => setConnection({ ...connection, database: n.target.value })}
                type='text'
                placeholder='mysql'
                required
              />
            </Col>
          </Row>

          { type !== 'mysql' && (
            <Row className='mb-3'>
              <Col lg='12'>
                <Form.Label>Schema ( OPTIONAL, case sensitive )</Form.Label>
                <Form.Control
                  size='sm'
                  value={connection.schemaName || ''}
                  onChange={(n) => setConnection({ ...connection, schemaName: n.target.value })}
                  type='text'
                  placeholder='PUBLIC'
                />
              </Col>
            </Row>
          )}
        </>
      )}

      {showAuthModeDropdown && (
        <Row className='mb-3'>
          <Col lg='12'>
            <Form.Label>Authentication mode</Form.Label>
            <Form.Control
              as='select'
              size='sm'
              data-testid="auth-mode-select"
              value={authMode}
              onChange={(n) => setConnection({ ...connection, authMode: n.target.value, cloudAccountId: '' })}
            >
              {authModeOptions.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Form.Control>
            <Form.Text className='text-muted'>{selectedOption.helper}</Form.Text>
          </Col>
        </Row>
      )}

      {showCredentialSource && (
        <Row className='mb-3'>
          <Col lg='12'>
            <Form.Label>Credential source</Form.Label>
            <Form.Control
              as='select'
              size='sm'
              data-testid="credential-source-select"
              value={connection.cloudAccountId || ''}
              onChange={(n) => setConnection({ ...connection, cloudAccountId: n.target.value || null })}
            >
              <option value=''>Default (platform identity)</option>
              {credentialSourceOptions.map(c => (
                <option key={c.id} value={c.id}>{c.displayName}</option>
              ))}
            </Form.Control>
            <Form.Text className='text-muted'>
              Default uses the platform's workload identity. Pick an alternate credential only for data sources that identity cannot reach.
            </Form.Text>
          </Col>
        </Row>
      )}

      <Row className='mb-3'>
        <Col lg={passwordRequired ? '6' : '12'} className={passwordRequired ? 'pr-1' : ''}>
          <Form.Label>{usernameRequired ? 'Username *' : 'Username ( OPTIONAL )'}</Form.Label>
          <Form.Control
            size='sm'
            type='text'
            value={connection.user}
            autoComplete='new-password'
            onChange={(n) => setConnection({ ...connection, user: n.target.value})}
            placeholder={authMode === 'AWS_IAM' ? 'DB user mapped to the IAM identity (optional)' :
                         authMode === 'AZURE_ENTRA' ? 'AAD user / DB login (optional)' : 'Username'}
            required={usernameRequired}
          />
        </Col>
        {passwordRequired && (
          <Col lg='6' className='pl-1'>
            <Form.Label>Password{id ? '' : ' *'}</Form.Label>
            <Form.Control
              size='sm'
              type='password'
              value={connection.password ?? ''}
              autoComplete={id ? 'off' : 'new-password'}
              placeholder={id ? 'Leave blank to keep current; enter new password to change' : 'Password'}
              onChange={(n) => setConnection({ ...connection, password: n.target.value })}
              required={!id}
            />
          </Col>
        )}
      </Row>

      {showRegionField && (
        <Row className='mb-3'>
          <Col lg='12'>
            <Form.Label>AWS region ( OPTIONAL )</Form.Label>
            <Form.Control
              size='sm'
              type='text'
              value={connection.region ?? ''}
              onChange={(n) => setConnection({ ...connection, region: n.target.value })}
              placeholder='Leave blank to infer from the RDS hostname (e.g. us-east-1)'
            />
          </Col>
        </Row>
      )}

      {isSqlServer && (
        <Row className='mb-3'>
          <Col lg='12'>
            <Form.Check
              type='switch'
              id='mssql-encrypt'
              label='Encrypt connection'
              checked={encrypt}
              onChange={(n) => setConnection({ ...connection, encrypt: n.target.checked })}
            />
            <Form.Text className='text-muted'>
              Negotiate TLS with the SQL Server. Required by SQL Server JDBC driver v10+ unless explicitly disabled.
            </Form.Text>
            <Form.Check
              className='mt-2'
              type='switch'
              id='mssql-trust-cert'
              label='Trust certificate'
              checked={trustServerCertificate}
              onChange={(n) => setConnection({ ...connection, trustServerCertificate: n.target.checked })}
            />
            <Form.Text className='text-muted'>
              Skip server certificate validation. Enable when using a self-signed certificate or an internal CA that the platform's Java truststore does not recognize.
            </Form.Text>
          </Col>
        </Row>
      )}

      {/* <Row className='mb-3'>
        <Col>
          <CronJobEditor
            scheduleExpression={connection.scheduleExpression}
            scheduleRules={connection.scheduleRules}
            setScheduleData={(v) => setConnection({ ...connection, scheduleExpression: v.scheduleExpression, scheduleRules: v.scheduleRules })}
          />
        </Col>
      </Row> */}

      <Row className='mb-3'>
        <Col lg='12'>
          <Form.Check
            type='switch'
            id='advanced-toggle'
            label='Advanced connection options'
            checked={!!connection._showAdvanced}
            onChange={(n) => setConnection({ ...connection, _showAdvanced: n.target.checked })}
          />
        </Col>
      </Row>

      {connection._showAdvanced && (
        <Row className='mb-3'>
          <Col lg='12'>
            <Form.Group>
              <Form.Label>Connection properties</Form.Label>
              <Form.Control
                as='textarea'
                rows={4}
                value={connection.connectionProperties ?? ''}
                onChange={(n) => setConnection({ ...connection, connectionProperties: n.target.value })}
                placeholder={'ApplicationName=c2-discover\ntcpKeepAlive=true\n# lines beginning with # are ignored'}
                spellCheck={false}
              />
              <Form.Text className='text-muted'>
                One <code>KEY=VALUE</code> per line. Merged into the JDBC properties at connect
                time; username/password from the credentials above always take precedence.
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>
      )}
    </div>
  );
}

export default DbSourceEditor;
